import { LexicalComposer } from '@lexical/react/LexicalComposer'
import { ContentEditable } from '@lexical/react/LexicalContentEditable'
import { EditorRefPlugin } from '@lexical/react/LexicalEditorRefPlugin'
import { LexicalErrorBoundary } from '@lexical/react/LexicalErrorBoundary'
import { OnChangePlugin } from '@lexical/react/LexicalOnChangePlugin'
import { PlainTextPlugin } from '@lexical/react/LexicalPlainTextPlugin'
import clsx from 'clsx'
import {
	$createParagraphNode,
	$createTextNode,
	$getRoot,
	type EditorState,
	type LexicalEditor as LexicalEditorType,
} from 'lexical'
import {
	BeautifulMentionComponentProps,
	createBeautifulMentionNode,
} from 'lexical-beautiful-mentions'
import { isEmpty } from 'lodash-es'
import {
	type ComponentProps,
	ElementType,
	type Ref,
	useCallback,
	useImperativeHandle,
	useRef,
} from 'react'
import { useMount } from 'react-use'
import styles from './LexicalEditor.module.css'
import LexicalMentionNode, { userMentionsTheme } from './LexicalMentionNode'
import LexicalMentionUserPlugin from './LexicalMentionUserPlugin'
import SubmitShortcutPlugin from './SubmitShortcutPlugin'

export interface LexicalEditorHandle {
	editor: LexicalEditorType | null
	clear: () => void
	getText: () => Promise<string>
	setText: (text: string) => void
}

export interface LexicalEditorProps extends Omit<
	ComponentProps<typeof ContentEditable>,
	'onChange' | 'value' | 'defaultValue' | 'ref'
> {
	onChange?: (value: string, editorState: EditorState) => void
	onSubmit?: () => void
	value?: string
	defaultValue?: string
	ref?: Ref<LexicalEditorHandle>
}

export default function LexicalEditor({
	onChange,
	onSubmit,
	placeholder,
	defaultValue,
	value,
	className,
	ref,
	...props
}: LexicalEditorProps) {
	const editorRef = useRef<LexicalEditorType | null>(null)

	const getText = useCallback((): Promise<string> => {
		return new Promise<string>((resolve, reject) => {
			editorRef.current?.getEditorState().read(() => {
				resolve($getRoot().getTextContent())
			})
		})
	}, [])

	const setText = useCallback((text: string) => {
		editorRef.current?.update(() => {
			const root = $getRoot()
			root.clear()
			const textNode = $createTextNode(text)
			const paraNode = $createParagraphNode().append(textNode)
			root.append(paraNode)
			root.selectEnd()
		})
	}, [])

	const clear = useCallback(
		() =>
			new Promise<void>((resolve) => {
				setTimeout(() => {
					editorRef.current?.update(() => {
						const root = $getRoot()
						root.clear()
						resolve()
					})
				}, 0)
			}),
		[]
	)

	useImperativeHandle(
		ref,
		() => ({
			get editor() {
				return editorRef.current
			},
			getText,
			setText,
			set content(content: string) {
				const state = editorRef.current?.parseEditorState(content)
				if (state) {
					editorRef.current?.setEditorState(state)
				}
			},
			clear,
		}),
		[getText, clear, setText]
	)

	useMount(() => {
		if (isEmpty(value)) {
			return
		}

		setText(value as string)
	})

	return (
		<LexicalComposer
			initialConfig={{
				namespace: 'jike-editor',
				onError: console.error,
				nodes: [
					...createBeautifulMentionNode(
						LexicalMentionNode as ElementType<BeautifulMentionComponentProps>
					),
				],
				theme: {
					beautifulMentions: userMentionsTheme,
				},
			}}
		>
			<div className={clsx(styles.container, className)}>
				<PlainTextPlugin
					contentEditable={
						<ContentEditable
							aria-placeholder=""
							placeholder={(isEditable: boolean) => (
								<div className={styles.placeholder}>
									{typeof placeholder === 'function' ? placeholder(isEditable) : placeholder}
								</div>
							)}
							className={styles.contentEditable}
							{...props}
						/>
					}
					ErrorBoundary={LexicalErrorBoundary}
				/>
			</div>

			<EditorRefPlugin editorRef={editorRef} />

			<OnChangePlugin
				onChange={(state, editor) => {
					getText().then((text) => {
						onChange?.(text, state)
					})
				}}
			/>

			<LexicalMentionUserPlugin />

			{onSubmit && <SubmitShortcutPlugin onSubmit={onSubmit} />}
		</LexicalComposer>
	)
}
